import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { TranslatableText } from '@/components/ui/TranslatableText'
import type { ForensicsEnvironment } from '@/types/entities'

interface EnvironmentActionsProps {
    environment: ForensicsEnvironment
    onEdit: (env: ForensicsEnvironment) => void
    onDelete: (env: ForensicsEnvironment) => void
    onView: (env: ForensicsEnvironment) => void
}

function EnvironmentActions({ environment, onEdit, onDelete, onView }: EnvironmentActionsProps) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onView(environment)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(environment)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(environment)} className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export function createForensicsColumns(
    onEdit: (env: ForensicsEnvironment) => void,
    onDelete: (env: ForensicsEnvironment) => void,
    onView: (env: ForensicsEnvironment) => void,
    t: (key: string) => string
): ColumnDef<ForensicsEnvironment>[] {
    return [
        {
            accessorKey: 'name',
            header: t('forensics.environmentName'),
            cell: ({ row }) => (
                <div className="font-medium"><TranslatableText text={row.original.name} /></div>
            ),
        },
        {
            accessorKey: 'securityGroupId',
            header: t('forensics.securityGroup'),
            cell: ({ row }) => (
                <div className="text-sm font-mono">{row.original.securityGroupId}</div>
            ),
        },
        {
            accessorKey: 'awsAccountId',
            header: t('forensics.awsAccount'),
            cell: ({ row }) => (
                <div className="text-sm font-mono">{row.original.awsAccountId}</div>
            ),
        },
        {
            id: 'servers',
            header: t('forensics.servers'),
            cell: ({ row }) => (
                <div className="text-sm">{row.original.servers?.length ?? 0}</div>
            ),
        },
        {
            id: 'runningVersions',
            header: t('forensics.runningVersions'),
            cell: ({ row }) => {
                const running = row.original.objectVersions?.filter(v => v.status === 'running' || v.status === 'pending') ?? []
                return (
                    <Badge variant={running.length > 0 ? 'default' : 'secondary'}>
                        {running.length}
                    </Badge>
                )
            },
        },
        {
            accessorKey: 'status',
            header: t('forensics.status'),
            cell: ({ row }) => {
                const status = row.original.status
                return (
                    <Badge variant={status === 'active' ? 'default' : 'secondary'}>
                        {status === 'active' ? t('forensics.active') : t('forensics.inactive')}
                    </Badge>
                )
            },
        },
        {
            id: 'actions',
            header: t('common.actions'),
            cell: ({ row }) => (
                <EnvironmentActions
                    environment={row.original}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onView={onView}
                />
            ),
        },
    ]
}
